'use client'

import { useEffect } from 'react'

interface LocalmindAiProps {
  id: string
}

const EMBED_SRC = 'https://www.chatbase.co/embed.min.js'

const chatbaseScripts = () =>
  Array.from(document.querySelectorAll<HTMLScriptElement>(`script[src="${EMBED_SRC}"]`))

/**
 * Removes a booted Chatbase widget completely. The embed ignores every further
 * script while `window.__chatbaseWidgetInitialized` is set, so switching to
 * another chatbot needs the flag, the global API, the script tags and the DOM
 * the embed created gone first.
 */
const removeChatbase = () => {
  chatbaseScripts().forEach((script) => script.remove())
  document.querySelectorAll('[id^="chatbase-"]').forEach((el) => el.remove())
  // Only the embed's own stylesheet: globals.css styles #chatbase-bubble-button
  // too, and in development it is injected as a <style> tag as well.
  document.querySelectorAll('style').forEach((style) => {
    if (style.textContent?.includes('@keyframes chatbase-bubble-entrance')) style.remove()
  })
  delete window.chatbase
  delete window.__chatbaseWidgetInitialized
}

const loadChatbase = (id: string) => {
  // Queue calls made before the embed has loaded; the embed replays `q`.
  const chatbaseFn = ((...args: unknown[]) => {
    chatbaseFn.q.push(args)
  }) as unknown as NonNullable<Window['chatbase']> & { q: unknown[][] }
  chatbaseFn.q = []

  window.chatbase = new Proxy(chatbaseFn, {
    get(target, prop: string | symbol) {
      if (prop === 'q') {
        return target.q
      }
      return (...args: unknown[]) => target(String(prop), ...args)
    },
  })

  const script = document.createElement('script')
  script.src = EMBED_SRC
  script.id = id
  script.setAttribute('domain', 'www.chatbase.co')
  document.body.appendChild(script)
}

function LocalmindAi({ id }: LocalmindAiProps) {
  useEffect(() => {
    if (!id) {
      return
    }

    let loadHandler: (() => void) | null = null

    // The same chatbot is already running, e.g. when moving between lessons of
    // one chapter: keep it, including an open conversation.
    const scripts = chatbaseScripts()
    const alreadyRunning = scripts.length === 1 && scripts[0]?.id === id

    if (!alreadyRunning) {
      // Another chapter's chatbot (or a leftover): Chatbase would keep showing it.
      removeChatbase()
      if (document.readyState === 'complete') {
        loadChatbase(id)
      } else {
        loadHandler = () => loadChatbase(id)
        window.addEventListener('load', loadHandler, { once: true })
      }
    }

    // Registered in both cases, so leaving the academy always removes the widget.
    return () => {
      if (loadHandler) {
        window.removeEventListener('load', loadHandler)
      }
      // Runs after the next page has mounted: inside the academy that page's
      // widget decides whether to keep or replace this one.
      setTimeout(() => {
        if (!window.location.pathname.startsWith('/academy')) {
          removeChatbase()
        }
      }, 0)
    }
  }, [id])

  if (!id) {
    return null
  }

  return <div />
}

export default LocalmindAi
